import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Images, X } from "lucide-react";
import SmartImage from "@/components/ui/SmartImage";
import { normalizeImgUrl } from "@/lib/utils-img";

/**
 * Álbum de fotos público: rejilla de miniaturas y visor a pantalla completa.
 *
 * `fotos` es `[{ url, caption }]` (columna `gallery`). Sin dependencias: el
 * visor se maneja con flechas, teclado (← → Esc) y deslizando en el móvil, que
 * es como la mayoría lo verá —también desde la app, que abre la noticia de la
 * web en su navegador integrado—.
 *
 * `referrerPolicy="no-referrer"`: las fotos enlazadas desde Google Drive o
 * Google Fotos a veces se niegan a cargar si ven que las pide otra web.
 */
export default function GaleriaFotos({ fotos, titulo = "Galería de fotos" }) {
  const lista = (Array.isArray(fotos) ? fotos : []).filter(f => f && f.url);
  const [abierta, setAbierta] = useState(null); // índice o null

  if (!lista.length) return null;

  return (
    <section className="mt-12" aria-labelledby="galeria-titulo" data-testid="galeria-fotos">
      <h2 id="galeria-titulo" className="font-heading text-2xl font-bold mb-5">
        {titulo} <span className="text-base font-normal text-muted-foreground">({lista.length})</span>
      </h2>
      <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
        {lista.map((f, i) => (
          <li key={f.url}>
            <button
              type="button"
              onClick={() => setAbierta(i)}
              className="block w-full rounded-xl overflow-hidden focus-ring group"
              aria-label={f.caption ? `Ver foto: ${f.caption}` : `Ver foto ${i + 1} de ${lista.length}`}
              data-testid={`galeria-miniatura-${i}`}
            >
              <SmartImage
                src={f.url}
                alt={f.caption || ""}
                ratio="1 / 1"
                referrerPolicy="no-referrer"
                imgClassName="group-hover:scale-105 transition-transform duration-500"
              />
            </button>
          </li>
        ))}
      </ul>
      {abierta !== null && (
        <Visor fotos={lista} inicio={abierta} onClose={() => setAbierta(null)} />
      )}
    </section>
  );
}

/**
 * Para las secciones que se muestran como tarjetas (eventos, formación,
 * recursos, podcast): una tira de miniaturas con el botón «Ver fotos» que abre
 * el mismo visor. En una tarjeta no cabe la rejilla entera de la noticia.
 */
export function AlbumCompacto({ fotos, className = "" }) {
  const lista = (Array.isArray(fotos) ? fotos : []).filter(f => f && f.url);
  const [abierta, setAbierta] = useState(null);
  if (!lista.length) return null;
  const muestra = lista.slice(0, 4);

  return (
    <div className={className} data-testid="album-compacto">
      <button
        type="button"
        onClick={() => setAbierta(0)}
        className="w-full flex items-center gap-3 rounded-xl p-2 bg-muted/60 hover:bg-muted transition-colors focus-ring text-left"
        aria-label={`Ver álbum de fotos (${lista.length})`}
      >
        <span className="flex -space-x-3 flex-shrink-0">
          {muestra.map((f, i) => (
            <img
              key={f.url}
              src={normalizeImgUrl(f.url)}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              className="w-10 h-10 rounded-lg object-cover ring-2 ring-card bg-muted"
              style={{ zIndex: muestra.length - i }}
            />
          ))}
        </span>
        <span className="flex items-center gap-1.5 text-sm font-semibold text-primary">
          <Images className="w-4 h-4" aria-hidden="true" />
          Ver fotos ({lista.length})
        </span>
      </button>
      {abierta !== null && (
        <Visor fotos={lista} inicio={abierta} onClose={() => setAbierta(null)} />
      )}
    </div>
  );
}

function Visor({ fotos, inicio, onClose }) {
  const [i, setI] = useState(inicio);
  const toque = useRef(null);
  const cerrarRef = useRef(null);
  const total = fotos.length;
  const ir = useCallback(d => setI(v => (v + d + total) % total), [total]);

  useEffect(() => {
    const previo = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cerrarRef.current?.focus();
    const tecla = e => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") ir(1);
      else if (e.key === "ArrowLeft") ir(-1);
    };
    window.addEventListener("keydown", tecla);
    return () => {
      window.removeEventListener("keydown", tecla);
      document.body.style.overflow = overflow;
      previo?.focus?.();
    };
  }, [ir, onClose]);

  const foto = fotos[i];

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Visor de fotos"
      data-testid="galeria-visor"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={e => { toque.current = e.touches[0].clientX; }}
      onTouchEnd={e => {
        if (toque.current === null) return;
        const dx = e.changedTouches[0].clientX - toque.current;
        toque.current = null;
        if (Math.abs(dx) > 50) ir(dx < 0 ? 1 : -1);
      }}
    >
      <div className="flex items-center justify-between px-4 py-3 text-white/80 text-sm">
        <span aria-live="polite">{i + 1} / {total}</span>
        <button ref={cerrarRef} type="button" onClick={onClose} aria-label="Cerrar"
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 focus-ring">
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="relative flex-1 flex items-center justify-center px-2 sm:px-16 min-h-0"
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <img
          key={foto.url}
          src={normalizeImgUrl(foto.url)}
          alt={foto.caption || ""}
          referrerPolicy="no-referrer"
          className="max-w-full max-h-full object-contain select-none"
          draggable={false}
        />
        {total > 1 && (
          <>
            <button type="button" onClick={() => ir(-1)} aria-label="Foto anterior"
              className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center focus-ring">
              <ChevronLeft className="w-7 h-7" />
            </button>
            <button type="button" onClick={() => ir(1)} aria-label="Foto siguiente"
              className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center focus-ring">
              <ChevronRight className="w-7 h-7" />
            </button>
          </>
        )}
      </div>

      <p className="min-h-[3.5rem] px-6 py-4 text-center text-white/90 text-sm leading-relaxed">
        {foto.caption}
      </p>
    </div>,
    document.body
  );
}
